// Catálogo do grupo Admin da barra lateral.
//
// Por que um arquivo próprio, e não `menu-items.ts`: o catálogo principal é
// filtrado por perfil, realm e simulação (`MenuService.getMenuItems()`), e o
// grupo Admin não passa por nada disso — ele aparece inteiro para
// `profile_id === ADMINISTRATOR` e some inteiro para o resto. Misturar os dois
// obrigaria todo item daqui a carregar `profiles`, `global` e `simulated` só
// para satisfazer o tipo.
//
// Por que tipo próprio, e não `MenuItem`: `MenuItem` exige `path: string` e
// `profiles: string[]`, e não tem como expressar destino externo (o Horizon
// mora fora do Angular, atrás do `baseUrl`). O tipo daqui diz só o que este
// grupo precisa.
//
// Desde 28/09/2026 este é o ÚNICO lugar onde o administrador encontra tela de
// back-office: as entradas de `menu-items.ts` que nenhum perfil de cliente vê
// (Provedores, Fracionamentos, Fiscal, os relatórios internos…) saem da
// navegação principal dele (`MenuService.getMenuItems()`) e moram aqui, nas
// seções Operação e Relatórios. O spec do componente confere que nenhuma se
// perdeu no caminho.
//
// Para acrescentar um item: escolha a seção, some um objeto em `items` com
// `label` (pt-BR), `icon` (FontAwesome 4.7, sem o prefixo `fa`), `path` OU
// `externalPath`, e um `testId` estável. A cor vem da seção — item não escolhe
// cor. Para acrescentar uma SEÇÃO é preciso também uma cor nova: ver
// `_palette.scss` → `theme.scss` → `variables.scss`.

/** Uma entrada folha do grupo Admin. */
export interface AdminMenuItem {
  /** Rótulo visível, em português. */
  label: string;
  /** Classe do FontAwesome 4.7, já sem o `fa` de família (ex.: `fa-server`). */
  icon: string;
  /** Rota interna do Angular. Excludente com `externalPath`. */
  path?: string;
  /**
   * Caminho relativo ao `environment.baseUrl` — abre em aba nova. É o caso das
   * telas do Laravel, que não são rota do painel.
   */
  externalPath?: string;
  /** Valor do `data-testid`; os specs e o Cypress dependem dele. */
  testId: string;
  /**
   * Realce só na rota exata: para o item cuja rota é prefixo de outro item do
   * Admin (Configurações Globais × Etiqueta Novo no menu), senão os dois acendem.
   */
  exact?: boolean;
  /**
   * Selo fixo ao lado do rótulo (ex.: "Novo" no Acompanhamento de agentes RCS, 17tm466frtx).
   * Mesmo visual (`.menu-badge`) do selo configurável das Configurações Globais, mas não
   * passa pelo `MenuNewBadgesService`: este não se apaga ao clicar, é fixo até alguém tirar
   * daqui. Pouco usado de propósito — um selo permanente em todo item perde a força.
   */
  badge?: string;
}

/**
 * Um bloco do grupo Admin. `colorToken` é o NOME da custom property da cor da
 * seção — o template monta `var(<colorToken>)` e o SCSS lê `--section-color`.
 * Guardar o nome, e não o valor, é o que mantém a cor viajando pelo fluxo
 * `_palette.scss` → `theme.scss` → componente, com a versão internacional
 * redefinindo o token sem tocar em TypeScript.
 */
export interface AdminMenuSection {
  /** Identificador da seção; também é a chave de `openSubmenus`. */
  key: string;
  label: string;
  icon: string;
  colorToken: string;
  items: AdminMenuItem[];
}

export const adminMenuSections: AdminMenuSection[] = [
  {
    key: 'infra',
    label: 'Infra',
    icon: 'fa-server',
    colorToken: '--sms-admin-infra',
    items: [
      // O Pulse saiu em 28/09/2026: ninguém mais olhava para ele.
      { label: 'Horizon', icon: 'fa-tachometer', externalPath: '/monitoring', testId: 'horizon' }
    ]
  },
  {
    // O prefixo "IA - " saiu dos rótulos: a seção já diz isso, e repetir
    // roubava a primeira palavra de cada linha, que é onde o olho bate.
    key: 'ai',
    label: 'IA',
    icon: 'fa-magic',
    colorToken: '--sms-admin-ai',
    items: [
      { label: 'Configurações', icon: 'fa-cog', path: '/admin/ai-settings', testId: 'admin-ai-settings' },
      { label: 'Modelos', icon: 'fa-table', path: '/admin/ai-models', testId: 'admin-ai-models' },
      // Custos e markup só valem para a IA (07/10/2026, Bruno): saíram de Preços.
      { label: 'Custos', icon: 'fa-money', path: '/admin-pricing/service-costs', testId: 'admin-pricing-service-costs' },
      { label: 'Markup', icon: 'fa-percent', path: '/admin-pricing/markups', testId: 'admin-pricing-markups' },
      { label: 'Prompts de Evento', icon: 'fa-comments', path: '/admin/ai-event-prompts', testId: 'admin-ai-event-prompts' },
      { label: 'Regras Internas', icon: 'fa-shield', path: '/admin/ai-system-prompts', testId: 'admin-ai-system-prompts' },
      { label: 'Uso por Cliente', icon: 'fa-bar-chart', path: '/admin/ai-usage-report', testId: 'admin-ai-usage-report' }
    ]
  },
  {
    // RCS (17tm466frtx). Seção própria, e não dentro de Operação, pelo mesmo motivo
    // da IA: é um canal com telas que só existem por causa dele (acompanhamento dos agentes,
    // métricas do cache e da fatura do broker). Fracionamentos continua em
    // Operação, porque a tela é a mesma para SMS e RCS — só ganhou tabs.
    key: 'rcs',
    label: 'RCS',
    icon: 'fa-commenting',
    colorToken: '--sms-admin-rcs',
    items: [
      // Acompanhamento (17tm466frtx, 22/10/2026): onde está cada agente de cada cliente, etapa
      // a etapa, com quem está esperando quem. Desde 29/10 também faz o que a "Fila de agentes"
      // fazia (a Fila saiu do menu e a rota antiga redireciona para cá). Rótulo curto "RCS"
      // porque "Acompanhamento" cortava no menu; o título da tela segue "RCS · Acompanhamento".
      // Selo fixo "Novo" — mesmo texto do grupo RCS do cliente (`menu-items.ts`).
      { label: 'RCS', icon: 'fa-binoculars', path: '/admin/rcs/acompanhamento', testId: 'admin-rcs-acompanhamento', badge: 'Novo' },
      { label: 'Métricas', icon: 'fa-line-chart', path: '/admin/rcs/metrics', testId: 'admin-rcs-metrics' }
    ]
  },
  {
    key: 'pricing',
    label: 'Preços',
    icon: 'fa-tags',
    colorToken: '--sms-admin-pricing',
    items: [
      { label: 'Preço padrão e assinante', icon: 'fa-tag', path: '/admin-pricing/service-prices', testId: 'admin-pricing-service-prices' },
      { label: 'Contratos por conta', icon: 'fa-handshake-o', path: '/admin-pricing/price-overrides', testId: 'admin-pricing-price-overrides' },
      { label: 'Bônus de recarga', icon: 'fa-gift', path: '/admin-pricing/bonus-tiers', testId: 'admin-pricing-bonus-tiers' },
      { label: 'Câmbio', icon: 'fa-exchange', path: '/admin-pricing/fx-windows', testId: 'admin-pricing-fx-windows' },
      // Só na betaHml: preço do add-on de domínio próprio, que o cliente não vê.
      { label: 'Domínio próprio', icon: 'fa-link', path: '/dominio-proprio/precos', testId: 'admin-pricing-custom-domain' }
    ]
  },
  {
    key: 'wallet',
    label: 'Carteira',
    icon: 'fa-credit-card',
    colorToken: '--sms-admin-wallet',
    items: [
      { label: 'Migração para a carteira', icon: 'fa-random', path: '/admin-pricing/migration', testId: 'admin-pricing-migration' }
    ]
  },
  {
    // Telas de back-office que moravam soltas na navegação principal. As rotas
    // são as mesmas; só o endereço no menu mudou.
    key: 'operations',
    label: 'Operação',
    icon: 'fa-wrench',
    colorToken: '--sms-admin-operations',
    items: [
      { label: 'Provedores', icon: 'fa-server', path: '/provider', testId: 'admin-provider' },
      { label: 'Fracionamentos', icon: 'fa-list-alt', path: '/fractionations', testId: 'admin-fractionations' },
      { label: 'Validação SMS Massivo', icon: 'fa-check-square-o', path: '/sms-validation', testId: 'admin-sms-validation' },
      { label: 'Analisar SMS Massivo', icon: 'fa-filter', path: '/broadcasts-filter', testId: 'admin-broadcasts-filter' },
      { label: 'Analisar Sites', icon: 'fa-search', path: '/short-url-analysis', testId: 'admin-short-url-analysis' },
      { label: 'Usuários', icon: 'fa-users', path: '/users', testId: 'admin-users' },
      { label: 'Agências', icon: 'fa-bank', path: '/partner-management', testId: 'admin-partner-management' }
    ]
  },
  {
    key: 'reports',
    label: 'Relatórios',
    icon: 'fa-bar-chart',
    colorToken: '--sms-admin-reports',
    items: [
      { label: 'Faturamento', icon: 'fa-file', path: '/reports', testId: 'admin-reports' },
      { label: 'Consumo', icon: 'fa-file-text-o', path: '/consumption-report', testId: 'admin-consumption-report' },
      { label: 'Provedores', icon: 'fa-server', path: '/provider-report', testId: 'admin-provider-report' },
      { label: 'Acessos ao APP', icon: 'fa-mobile', path: '/app-authentication-report', testId: 'admin-app-authentication-report' },
      { label: 'Fiscal', icon: 'fa-newspaper-o', path: '/financial', testId: 'admin-financial' }
    ]
  },
  {
    key: 'system',
    label: 'Sistema',
    icon: 'fa-cogs',
    colorToken: '--sms-admin-system',
    items: [
      { label: 'Configurações Globais', icon: 'fa-sliders', path: '/admin-configurations', testId: 'admin-configurations', exact: true },
      // Quais itens do menu do cliente mostram a etiqueta "Novo" (CU-17tm466mg60).
      { label: 'Etiqueta Novo no menu', icon: 'fa-certificate', path: '/admin-configurations/menu-new-badges', testId: 'admin-menu-new-badges' },
      { label: 'Tutoriais', icon: 'fa-files-o', path: '/tutoriais', testId: 'tutoriais' }
    ]
  }
];
